/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FaCheckCircle, FaSearch, FaTimesCircle } from 'react-icons/fa';
import { useQueryClient } from 'react-query';
import Select, { MultiValue, StylesConfig } from 'react-select';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import {
  DashboardHooks,
  useAgreementPlans,
  useAllMapIntegrationPlans,
  useErpPlans,
} from '@partiu/api/dashboard/hooks.ts';
import {
  IMapIntegrationPlanItem,
  IPlan,
  IPlanMappingRow,
  ICreateMapIntegrationPlanPayload,
} from '@partiu/api/dashboard/types.ts';
import { filterAndSortBySearchRelevance, getPlanSlugIntegrationForPayload } from './utils/searchPlanMapping.ts';
import { createMapIntegrationPlanStaff } from '@partiu/api/dashboard/api.ts';

type FilterType = 'all' | 'mapped' | 'unmapped';

function buildMappingIndex(mappings: IMapIntegrationPlanItem[]): Map<string, IMapIntegrationPlanItem[]> {
  const index = new Map<string, IMapIntegrationPlanItem[]>();
  for (const m of mappings) {
    const slug = m.plan_slug_integration;
    if (!slug) continue;
    const list = index.get(slug);
    if (list) list.push(m);
    else index.set(slug, [m]);
  }
  return index;
}

function formatPrice(value?: number | string): string {
  if (value === undefined || value === null) return '-';
  const num = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
  if (Number.isNaN(num)) return '-';
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

interface PlanOption {
  value: number;
  label: string;
  plan: IPlan;
}

interface BulkMapIntegrationPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  agreementId?: number;
}

export function BulkMapIntegrationPlanModal({
  isOpen,
  onClose,
  agreementId,
}: BulkMapIntegrationPlanModalProps) {
  const { colors } = useTheme();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState<FilterType>('unmapped');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedErpSlugs, setSelectedErpSlugs] = useState<Set<string>>(new Set());
  const [selectedPartiuPlans, setSelectedPartiuPlans] = useState<PlanOption[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const { data: erpData, isLoading: isLoadingErp } = useErpPlans();
  const { data: allMappings, isLoading: isLoadingMappings } = useAllMapIntegrationPlans();
  const { data: agreementPlansData } = useAgreementPlans();

  const erpPlans = erpData?.plans;
  const mappingIndex = useMemo(() => buildMappingIndex(allMappings || []), [allMappings]);

  const rows: IPlanMappingRow[] = useMemo(() => {
    if (!erpPlans) return [];
    return erpPlans.map((erpPlan) => {
      const planMappings = mappingIndex.get(erpPlan.slug) || [];
      return { ...erpPlan, isMapped: planMappings.length > 0, mappings: planMappings };
    });
  }, [erpPlans, mappingIndex]);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (filter === 'mapped') result = result.filter((r) => r.isMapped);
    if (filter === 'unmapped') result = result.filter((r) => !r.isMapped);
    if (searchTerm.trim()) {
      result = filterAndSortBySearchRelevance(result, searchTerm, true);
    }
    return result;
  }, [rows, filter, searchTerm]);

  const planOptions = useMemo(() => {
    const plans: IPlan[] = (agreementPlansData as any)?.results ?? agreementPlansData ?? [];
    if (!Array.isArray(plans)) return [];
    return plans.map((p: IPlan) => ({
      value: p.id,
      label: p.displayName || p.display_name || p.name || `Plano #${p.id}`,
      plan: p,
    }));
  }, [agreementPlansData]);

  const totalMapped = useMemo(() => rows.filter((r) => r.isMapped).length, [rows]);
  const totalUnmapped = useMemo(() => rows.filter((r) => !r.isMapped).length, [rows]);
  const totalMappingsToCreate = selectedErpSlugs.size * selectedPartiuPlans.length;

  const handleToggleErp = (slug: string) => {
    setSelectedErpSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const handleSelectAllErp = (checked: boolean) => {
    if (checked) {
      setSelectedErpSlugs(new Set(filteredRows.map((r) => r.slug)));
    } else {
      setSelectedErpSlugs(new Set());
    }
  };

  const allErpSelected =
    filteredRows.length > 0 && filteredRows.every((r) => selectedErpSlugs.has(r.slug));
  const someErpSelected = filteredRows.some((r) => selectedErpSlugs.has(r.slug));

  const handlePartiuChange = (selectedOptions: MultiValue<PlanOption>) => {
    setSelectedPartiuPlans(selectedOptions as PlanOption[]);
  };

  const handleClose = () => {
    if (isCreating) return;
    onClose();
    setSelectedErpSlugs(new Set());
    setSelectedPartiuPlans([]);
    setSearchTerm('');
    setFilter('unmapped');
    setProgress({ current: 0, total: 0 });
  };

  const handleSubmit = async () => {
    if (selectedErpSlugs.size === 0) {
      toast({ title: 'Selecione ao menos um plano ERP', status: 'warning', duration: 3000 });
      return;
    }
    if (selectedPartiuPlans.length === 0) {
      toast({ title: 'Selecione ao menos um plano', status: 'warning', duration: 3000 });
      return;
    }

    const slugs = Array.from(selectedErpSlugs);
    const partiuIds = selectedPartiuPlans.map((p) => p.value);
    const total = slugs.length * partiuIds.length;
    setProgress({ current: 0, total });
    setIsCreating(true);

    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    try {
      let done = 0;
      for (const slug of slugs) {
        const row = rows.find((r) => r.slug === slug);
        const planSlugIntegration = row ? getPlanSlugIntegrationForPayload(row) : slug;

        for (const planId of partiuIds) {
          const payload: ICreateMapIntegrationPlanPayload = {
            plan: planId,
            plan_slug_integration: planSlugIntegration,
          };
          if (agreementId) payload.agreement = agreementId;
          try {
            await createMapIntegrationPlanStaff(payload);
            successCount++;
          } catch (err: any) {
            errorCount++;
            const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Erro';
            errors.push(`${slug} → plano ${planId}: ${typeof msg === 'string' ? msg : JSON.stringify(msg)}`);
          }
          done++;
          setProgress({ current: done, total });
        }
      }

      // Invalidar queries após mapeamentos (atualiza comparação ERP ↔ mapeamentos)
      queryClient.refetchQueries([DashboardHooks.AllMapIntegrationPlans]);
      queryClient.refetchQueries([DashboardHooks.PlansIntegration]);
      queryClient.refetchQueries([DashboardHooks.ErpPlans]);

      if (successCount > 0) {
        queryClient.refetchQueries([DashboardHooks.Modules]);
        toast({
          title: errorCount === 0 ? 'Mapeamento em massa concluído' : 'Mapeamento parcialmente concluído',
          description: `${successCount} de ${total} mapeamento(s) criado(s)${errorCount > 0 ? `. ${errorCount} erro(s).` : ''}`,
          status: errorCount === 0 ? 'success' : 'warning',
          duration: 5000,
          isClosable: true,
        });
        handleClose();
      } else {
        toast({
          title: 'Erro ao criar mapeamentos',
          description: errors.slice(0, 2).join('; ') || 'Tente novamente.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (e) {
      toast({
        title: 'Erro inesperado',
        description: 'Não foi possível concluir o mapeamento em massa.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsCreating(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const selectStyles: StylesConfig<PlanOption, true> = {
    control: (provided) => ({
      ...provided,
      fontSize: '13px',
      minHeight: '38px',
      borderColor: selectedPartiuPlans.length === 0 ? '#E2E8F0' : '#3182ce',
    }),
    menu: (provided) => ({ ...provided, zIndex: 10, fontSize: '13px' }),
    multiValue: (provided) => ({ ...provided, backgroundColor: '#ebf8ff', borderRadius: '6px' }),
    multiValueLabel: (provided) => ({ ...provided, color: '#2b6cb0', fontSize: '13px' }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#2b6cb0',
      ':hover': { backgroundColor: '#bee3f8', color: '#1e4a72' },
    }),
    placeholder: (provided) => ({ ...provided, color: '#a0aec0', fontSize: '14px' }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '14px',
      backgroundColor: state.isSelected ? '#3182ce' : state.isFocused ? '#ebf8ff' : 'white',
      color: state.isSelected ? 'white' : '#2d3748',
    }),
  };

  const isLoading = isLoadingErp || isLoadingMappings;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent maxH="90vh">
        <ModalHeader fontSize="lg" color={colors.primaryColor} pb={2}>
          Mapeamento em massa
        </ModalHeader>
        <ModalCloseButton isDisabled={isCreating} />

        <ModalBody py={3} overflowY="auto">
          <VStack align="stretch" spacing={4}>
            {isCreating && progress.total > 0 && (
              <Box w="100%">
                <Flex justify="space-between" mb={1}>
                  <Text fontSize="sm" color="gray.600">Progresso</Text>
                  <Text fontSize="sm" fontWeight="600">{progress.current} / {progress.total}</Text>
                </Flex>
                <Progress value={(progress.current / progress.total) * 100} size="sm" colorScheme="blue" borderRadius="full" />
              </Box>
            )}

            <VStack align="stretch" spacing={4}>
              {/* Primeiro: Planos multi-select */}
              <Box>
                <Heading size="sm" mb={2} color="gray.700">
                  Planos
                </Heading>
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="medium" mb={1}>
                    Selecione os planos a vincular
                  </FormLabel>
                  <Select<PlanOption, true>
                    isMulti
                    options={planOptions}
                    value={selectedPartiuPlans}
                    onChange={handlePartiuChange}
                    placeholder="Buscar por nome do plano..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue?.trim() ? 'Nenhum plano encontrado para esta busca' : 'Nenhum plano encontrado'
                    }
                    isDisabled={isCreating}
                    styles={selectStyles}
                    closeMenuOnSelect={false}
                    isSearchable
                    menuPlacement="auto"
                    maxMenuHeight={200}
                    filterOption={(option, inputValue) => {
                      const search = (inputValue || '').trim().toLowerCase();
                      if (!search) return true;
                      const label = (option.label || '').toLowerCase();
                      return label.includes(search);
                    }}
                  />
                  {selectedPartiuPlans.length > 0 && (
                    <Text fontSize="xs" color="green.600" mt={1}>
                      ✓ {selectedPartiuPlans.length} plano(s) selecionado(s)
                    </Text>
                  )}
                </FormControl>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Cada plano ERP selecionado será vinculado a todos os planos escolhidos.
                </Text>
              </Box>

              {/* Segundo: Planos ERP com checkboxes */}
              <Box>
                <Heading size="sm" mb={2} color="gray.700">
                  Planos ERP
                </Heading>
                <HStack mb={2} spacing={2} flexWrap="wrap">
                  <Button
                    size="xs"
                    variant={filter === 'all' ? 'solid' : 'outline'}
                    colorScheme="gray"
                    onClick={() => setFilter('all')}
                    borderRadius="full"
                  >
                    Todos ({rows.length})
                  </Button>
                  <Button
                    size="xs"
                    variant={filter === 'mapped' ? 'solid' : 'outline'}
                    colorScheme="green"
                    onClick={() => setFilter('mapped')}
                    borderRadius="full"
                    leftIcon={<FaCheckCircle />}
                  >
                    Mapeados ({totalMapped})
                  </Button>
                  <Button
                    size="xs"
                    variant={filter === 'unmapped' ? 'solid' : 'outline'}
                    colorScheme="red"
                    onClick={() => setFilter('unmapped')}
                    borderRadius="full"
                    leftIcon={<FaTimesCircle />}
                  >
                    Sem mapeamento ({totalUnmapped})
                  </Button>
                </HStack>
                <InputGroup size="sm" mb={2}>
                  <InputLeftElement pointerEvents="none">
                    <FaSearch color="gray" />
                  </InputLeftElement>
                  <Input
                    placeholder="Buscar por ID, nome ou slug..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    bg="white"
                    borderColor="gray.200"
                  />
                </InputGroup>
                <Flex mb={2} align="center">
                  <Checkbox
                    size="md"
                    colorScheme="blue"
                    isChecked={allErpSelected}
                    isIndeterminate={someErpSelected && !allErpSelected}
                    onChange={(e) => handleSelectAllErp(e.target.checked)}
                    isDisabled={isCreating || filteredRows.length === 0}
                    sx={{
                      '& .chakra-checkbox__control': {
                        w: 5,
                        h: 5,
                        borderWidth: '2px',
                        borderColor: 'blue.300',
                        _checked: { borderWidth: '2px', borderColor: 'blue.600', bg: 'blue.500' },
                        _indeterminate: { borderWidth: '2px', borderColor: 'blue.500', bg: 'blue.400' },
                      },
                      '& .chakra-checkbox__label': { fontWeight: 500, fontSize: 'sm' },
                    }}
                  >
                    <Text fontSize="sm" fontWeight="500">Selecionar todos ({filteredRows.length} visíveis)</Text>
                  </Checkbox>
                </Flex>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor="gray.200"
                  maxH="280px"
                  overflowY="auto"
                  bg="gray.50"
                  p={2}
                >
                  {isLoading ? (
                    <Flex justify="center" py={8}>
                      <Spinner size="md" color={colors.primaryColor} />
                    </Flex>
                  ) : filteredRows.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                      Nenhum plano ERP encontrado
                    </Text>
                  ) : (
                    <VStack align="stretch" spacing={1}>
                      {filteredRows.map((row) => (
                        <Flex
                          key={row.id ?? row.slug}
                          align="center"
                          p={2}
                          borderRadius="md"
                          _hover={{ bg: 'white' }}
                          bg={selectedErpSlugs.has(row.slug) ? 'blue.50' : 'transparent'}
                        >
                          <Checkbox
                            size="md"
                            colorScheme="blue"
                            isChecked={selectedErpSlugs.has(row.slug)}
                            onChange={() => handleToggleErp(row.slug)}
                            isDisabled={isCreating}
                            mr={3}
                            flexShrink={0}
                            sx={{
                              '& .chakra-checkbox__control': {
                                w: 5,
                                h: 5,
                                borderWidth: '2px',
                                borderColor: 'blue.300',
                                _checked: { borderWidth: '2px', borderColor: 'blue.600', bg: 'blue.500' },
                              },
                            }}
                          />
                          <Box flex={1} minW={0}>
                            <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{row.name}</Text>
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>
                              {row.slug}
                              {row.price != null && !Number.isNaN(Number(row.price)) && ` · ${formatPrice(row.price)}`}
                            </Text>
                          </Box>
                          {row.isMapped && (
                            <Text fontSize="2xs" color="green.600" whiteSpace="nowrap">{row.mappings.length} mapeado(s)</Text>
                          )}
                        </Flex>
                      ))}
                    </VStack>
                  )}
                </Box>
              </Box>
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter pt={2} pb={3} borderTopWidth="1px" borderColor="gray.100" flexWrap="wrap" gap={2}>
          <HStack spacing={4} fontSize="xs" color="gray.500" fontWeight="normal" mr="auto">
            <Text as="span">{selectedErpSlugs.size} ERP</Text>
            <Text as="span">{selectedPartiuPlans.length} planos</Text>
            <Text as="span">{totalMappingsToCreate} mapeamento(s)</Text>
          </HStack>
          <HStack spacing={2}>
            <Button variant="ghost" size="sm" onClick={handleClose} isDisabled={isCreating}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              size="sm"
              onClick={handleSubmit}
              isLoading={isCreating}
              loadingText="Criando mapeamentos..."
              isDisabled={
                isCreating ||
                selectedErpSlugs.size === 0 ||
                selectedPartiuPlans.length === 0 ||
                isLoading
              }
            >
              Criar {totalMappingsToCreate > 0 ? `${totalMappingsToCreate} mapeamento(s)` : 'mapeamentos'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
